import { memo, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import { useDeleteClassTemplate } from "@/hooks/api/useClassTemplates"
import type { DeleteClassTemplateProps } from "@/types/class_template"
import { Button, Text } from "@chakra-ui/react"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"

const DeleteClassTemplate = memo(
  ({ class_template, onSuccess, onCancel }: DeleteClassTemplateProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const deleteClassTemplateMutation = useDeleteClassTemplate()

    const displayLabel = class_template.name ?? class_template.id

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleConfirm = async () => {
      try {
        await deleteClassTemplateMutation.mutateAsync(class_template.id)
        setIsOpen(false)
        onSuccess?.()
      } catch (error) {
        // Keep dialog open so user can retry after resolving error
        console.error("Error deleting classTemplate:", error)
      }
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          colorPalette="red"
          aria-label={`Delete classTemplate: ${displayLabel}`}
        >
          <FiTrash2 fontSize="14px" aria-hidden="true" />
          Delete
        </Button>

        <ConfirmDialog
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title="Delete ClassTemplate"
          message={
            <>
              <Text>
                Are you sure you want to delete <strong>{displayLabel}</strong>?
              </Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                This action cannot be undone. This classTemplate will be
                permanently removed.
              </Text>
            </>
          }
          confirmText="Delete ClassTemplate"
          confirmColorScheme="red"
          isLoading={deleteClassTemplateMutation.isPending}
        />
      </>
    )
  },
)

DeleteClassTemplate.displayName = "DeleteClassTemplate"

export default DeleteClassTemplate
export type { DeleteClassTemplateProps }
